import React from "react";
import { ArrowUpDown, Palette } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { t, useLang } from "../../lib/i18n";
import { useDesktop } from "../../lib/desktop";
import { RankEmblem } from "../rank/RankEmblem";
import { TurnRing } from "./TurnRing";
import { useSecondsLeft } from "../../lib/turnClock";

export const BottomActions = ({
  user,
  me,
  myHandRaw,
  isMyTurn,
  clock = null,
  isContinuer = false,
  sortMode,
  onSortChange,
  myColor,
  aboveBadge,
  emojiButton = null,
}) => {
  const { dir } = useLang();
  // A computer's large screen: a bigger avatar, and the name to match (lib/desktop.js).
  const big = useDesktop();
  // My turn's (or my vote's) last 15 seconds, under my name (the ring shows the rest).
  const secs = useSecondsLeft(clock, 15);
  const countdown = secs != null && (
    <span className={`text-[9px] tabular-nums leading-none ${secs <= 5 ? "text-rose-400" : "text-orange-300"}`} data-testid="turn-seconds">
      {t("game.timer.secs", { n: secs })}
    </span>
  );
  const continuerGreen = isContinuer && me?.has_played_nemna3;
  const continuerRed   = isContinuer && !me?.has_played_nemna3;

  const pillBorder = isMyTurn
    ? "border-amber-400/55"
    : continuerGreen
    ? "border-emerald-400/45"
    : continuerRed
    ? "border-rose-400/45"
    : "border-white/8";

  const pillGlow = isMyTurn
    ? { boxShadow: "0 0 0 1px rgba(251,191,36,0.12), 0 4px 24px rgba(0,0,0,0.65)" }
    : { boxShadow: "0 2px 14px rgba(0,0,0,0.55)" };

  const avatarRing = myColor
    ? { boxShadow: `0 0 0 2px ${myColor.border}` }
    : isMyTurn
    ? { boxShadow: "0 0 0 2px rgba(251,191,36,0.75)" }
    : { boxShadow: "0 0 0 1.5px rgba(255,255,255,0.14)" };

  return (
    <div className="flex items-end justify-center gap-2.5 mb-1">
      {/* The section around is click-through (Game.jsx): only the bar takes clicks. */}
      <div className="flex flex-col items-start gap-1 pointer-events-auto">
        {aboveBadge}

        {/* With the emoji button it fills a 360 px phone: a smaller gap there, and no « Trier » below (the icons say it). */}
        <div className="flex items-center gap-1 min-[400px]:gap-2">

          {/* ── Player info pill. No « شدّ الفريش » tag here: the others see it, not the player who chose it. ── */}
          <div
            className={`flex items-center gap-2.5 pl-1 pr-3.5 py-1 bg-black/50 backdrop-blur-2xl border ${pillBorder} rounded-2xl transition-all duration-300`}
            style={pillGlow}
            data-testid="my-player-info"
          >
            {/* Avatar + card count badge */}
            <div id={`player-icon-${user.user_id}`} className="relative flex-shrink-0">
              <AnimatePresence>
                {isMyTurn && (
                  <motion.div
                    key="turn-glow"
                    className="absolute -inset-1 rounded-full pointer-events-none"
                    animate={{ opacity: [0.55, 0.1, 0.55] }}
                    transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
                    style={{
                      background: "radial-gradient(circle, rgba(251,191,36,0.45) 0%, transparent 70%)",
                    }}
                  />
                )}
              </AnimatePresence>

              <Avatar className={`${big ? "h-10 w-10" : "h-7 w-7"} relative${isMyTurn && !clock ? " avatar-heartbeat" : ""}`} style={avatarRing}>
                <AvatarImage src={user.picture} />
                <AvatarFallback
                  className={`${big ? "text-[14px]" : "text-[10px]"} font-bold`}
                  style={
                    myColor
                      ? { background: myColor.bg, color: myColor.hex }
                      : { background: "rgba(6,78,59,0.85)", color: "#FCD34D" }
                  }
                >
                  {user.name?.[0]?.toUpperCase()}
                </AvatarFallback>
              </Avatar>

              {/* My turn's time (TurnRing) */}
              <TurnRing clock={clock} size={big ? 40 : 28} />

              {/* The player's rank (lib/ranks.js), on the avatar's lower corner */}
              {me?.rank && (
                <RankEmblem
                  rank={me.rank}
                  size={big ? 18 : 14}
                  className="absolute -bottom-1 -right-1.5 drop-shadow-[0_1px_1px_rgba(0,0,0,0.8)]"
                />
              )}

              {/* Card count */}
              <div className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 rounded-full bg-slate-900/95 border border-white/14 flex items-center justify-center px-1 shadow-sm">
                <span className="text-[8px] font-bold text-white/70 tabular-nums leading-none">
                  {myHandRaw.length}
                </span>
              </div>
            </div>

            {/* Name + status */}
            <div className="flex flex-col gap-[3px] min-w-0">
              <span className={`font-semibold text-white/92 truncate leading-none tracking-tight ${big ? "text-[13px] max-w-[120px]" : "text-[11px] max-w-[62px]"}`}>
                {user.name}
              </span>

              {isMyTurn ? (
                <div className="flex items-center gap-1 leading-none">
                  <motion.div
                    className="w-1.5 h-1.5 rounded-full bg-amber-400 flex-shrink-0"
                    animate={{ opacity: [1, 0.25, 1] }}
                    transition={{ duration: 1.1, repeat: Infinity }}
                  />
                  <span className="text-[9px] font-semibold text-amber-300/95 tracking-widest uppercase leading-none">
                    {t("bottom.yourTurn")}
                  </span>
                  {countdown}
                </div>
              ) : countdown || (
                <span className="text-[9px] text-white/28 font-medium leading-none">
                  {t("bottom.nCards", { n: myHandRaw.length })}
                </span>
              )}
            </div>
          </div>

          {emojiButton}

          {/* ── iOS-style segmented sort control, led by its label ("Trier │ Valeur Couleur").
               It follows the language's direction (the board itself stays LTR): in Tunisian the label comes first, on the right. ── */}
          <div
            dir={dir}
            className="flex items-center p-[3px] bg-black/40 backdrop-blur-2xl border border-white/8 rounded-2xl"
            style={{ boxShadow: "0 2px 10px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.04)" }}
          >
            <span className="hidden min-[360px]:inline ps-2 pe-1.5 text-[8px] font-semibold uppercase tracking-[0.16em] text-white/35 leading-none select-none">
              {t("bottom.sortBy")}
            </span>
            <span className="hidden min-[360px]:inline w-px h-3.5 bg-white/20 me-0.5" aria-hidden="true" />
            {[
              { mode: "rank", icon: <ArrowUpDown className="h-3 w-3" />, label: t("bottom.sortValue"), testId: "sort-rank-btn" },
              { mode: "suit", icon: <Palette     className="h-3 w-3" />, label: t("bottom.sortSuit"),  testId: "sort-suit-btn" },
            ].map(({ mode, icon, label, testId }) => (
              <button
                key={mode}
                onClick={() => onSortChange(mode)}
                data-testid={testId}
                className={[
                  "relative flex items-center gap-1 px-2.5 py-1.5 rounded-[14px]",
                  "text-[10px] font-medium transition-colors duration-150 select-none",
                  sortMode === mode ? "text-amber-300" : "text-white/38 hover:text-white/60",
                ].join(" ")}
              >
                {sortMode === mode && (
                  <motion.div
                    layoutId="sort-pill"
                    className="absolute inset-0 rounded-[14px] bg-amber-400/15"
                    style={{
                      boxShadow: "0 1px 4px rgba(0,0,0,0.4), inset 0 1px 0 rgba(251,191,36,0.12)",
                    }}
                    transition={{ type: "spring", stiffness: 480, damping: 36 }}
                  />
                )}
                <span className="relative z-10 flex items-center gap-1.5">
                  {icon}
                  {label}
                </span>
              </button>
            ))}
          </div>

        </div>
      </div>
    </div>
  );
};

export default BottomActions;
